import React from 'react';
import { useTranslation } from 'react-i18next';
import type { StatisticsSummaryView } from '../../api/generated/models/StatisticsSummaryView';
import { getDisplayCurrency, getAmountForCurrency, formatMoney } from '../../utils/statisticsHelpers';

interface TopsellerWidgetProps {
  data: StatisticsSummaryView | null;
  /** Shop host for constructing product image URLs. */
  shopHost?: string | null;
}

/**
 * ТЗ п.5: Top-10 products with name, artNr, quantitySold, revenue, thumbnail.
 * thumbnail from API is a relative path (cPfad from tartikelpict).
 * Full URL: https://{shopHost}/media/image/product/{productId}/xs/{thumbnail}
 */
export const TopsellerWidget: React.FC<TopsellerWidgetProps> = ({ data, shopHost }) => {
  const { t } = useTranslation();
  const currency = getDisplayCurrency(data);
  const products = React.useMemo(
    () => (data?.topProducts ?? []).filter((p: any) => (p?.productId ?? 0) > 0),
    [data?.topProducts]
  );

  const buildThumbnailUrl = (productId: number, thumbnail: string | null): string | null => {
    if (!thumbnail) return null;
    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) return thumbnail;
    if (!shopHost) return null;
    const host = shopHost.startsWith('http') ? shopHost : `https://${shopHost}`;
    return `${host.replace(/\/$/, '')}/media/image/product/${productId}/xs/${thumbnail}`;
  };

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_topseller')}</h3>
      </div>
      <div className="topseller-grid">
        {products.slice(0, 10).map((p: any) => {
          const thumbUrl = buildThumbnailUrl(p.productId, p.thumbnail ?? null);
          const revenue = getAmountForCurrency(p.amountByCurrency, currency);
          return (
            <div key={p.productId} className="topseller-item">
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt={p.productName}
                  className="topseller-item-image"
                  onError={(e) => {
                    // Fallback to placeholder on error
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove('topseller-item-image--hidden');
                  }}
                />
              ) : null}
              <div className={`topseller-item-image topseller-item-placeholder ${thumbUrl ? 'topseller-item-image--hidden' : ''}`}>
                -
              </div>
              <div className="topseller-item-info">
                <div className="topseller-item-name" title={p.productName}>
                  {p.productName || p.artNr}
                </div>
                {p.artNr && <div className="stat-metric-label">{p.artNr}</div>}
              </div>
              <div className="topseller-item-stats">
                <div className="topseller-item-qty">{p.quantitySold}</div>
                {revenue !== 0 && (
                  <div className="topseller-item-revenue">{formatMoney(revenue, currency)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
